// Copyright (c) 2021 WiseTime. All rights reserved.

import { Plural } from "@lingui/macro"
import React, { useMemo } from "react"
import styled from "styled-components/macro"
import { useSelector } from "react-redux"
import * as ToolbarSlice from "../toolbar/slice"
import * as TimelineSlice from "../slices/timeline"
import * as UserContextSlice from "../../user-context/slice"
import { getTimeBlocks, TimeBlock } from "../timeBlocks"
import { isFutureDate, TimelineDate } from "features/timeline/dateTime"
import { RootState } from "app/store"

const Label = styled.span`
  display: block;
  color: ${({ theme }): string => theme.colors.silverChalice};
  font-size: 10px;
  font-weight: 600;
  text-transform: none;
  font-style: normal;
`

type Props = {
  timelineDate: TimelineDate,
}

const LabelMatched = ({
  timelineDate,
}: Props): React.ReactElement<Props> | null => {
  const makeSelectTimeRowsForDate = useMemo(TimelineSlice.selectTimeRowsForCustomDate, [])
  const timeRowsForDate = useSelector((state: RootState) => makeSelectTimeRowsForDate(state, timelineDate))
  const makeSelectCollapsibleTimeGroupsCountForDate = useMemo(TimelineSlice.selectCollapsibleTimeGroupsCountForCustomDate, [])
  const collapsibleTimeGroupsForDateCount = useSelector((state: RootState) =>
    makeSelectCollapsibleTimeGroupsCountForDate(state, timelineDate)
  )
  const timeZoneName = useSelector(TimelineSlice.selectTimeZone)?.name
  const toolbarState = useSelector(ToolbarSlice.selectToolbarState)
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const showSelectedTimeRows = useSelector(TimelineSlice.selectShowSelectedTimeRows)
  const activeTimeRowIds = useSelector(TimelineSlice.selectActiveTimeRowsIds)
  const matchCount = timeZoneName ? getTimeBlocks(
    timeRowsForDate.filter(timeRow => timeRow.timeGroupId === ""),
    timeZoneName,
    {
      toolbarState,
      showSelectedTimeRows,
      currentSiteId,
    },
    [],
    activeTimeRowIds,
  ).reduce(
    (result: number, timeBlock: TimeBlock) => result + timeBlock.timelineRows.length,
    collapsibleTimeGroupsForDateCount,
  ) : collapsibleTimeGroupsForDateCount

  if (isFutureDate(timelineDate)) {
    return null
  }

  return (
    <Label>
      <Plural
        id="label_matches"
        value={matchCount}
        one="# match"
        other="# matches"
      />
    </Label>
  )
}

export default LabelMatched
